/**
 * Whether a set-password link is already past its expiry, read off the token.
 *
 * Portal invites and password resets are FastAPI-Users JWTs
 * (`portal_invite.py` → `fastapi_users.jwt.generate_jwt`), so the expiry sits
 * in the payload in plain sight. Reading it lets the screen say "this invite has
 * expired" on arrival, instead of after the customer has composed a password
 * that satisfies four policy rules (P1, client-onboarding portal walk
 * 2026-09-22).
 *
 * This is a courtesy, never a control. The signature is not checked and cannot
 * be — the secret is the server's — and a token can be dead for reasons its
 * payload does not show (already used: the password fingerprint moved on). The
 * server still decides on submit, exactly as before. So every doubt resolves
 * toward showing the form:
 *
 * - An unreadable token returns false. The server will say what it is.
 * - A grace window absorbs clock drift. Wrongly calling a live link dead needs
 *   the reader's clock to run ahead by more than the link has left to live, so
 *   a fresh 7-day invite is safe against any plausible drift; only a link
 *   already minutes from expiry is exposed, and that one is nearly dead anyway.
 */
export const EXPIRY_GRACE_MS = 5 * 60 * 1000

export function tokenExpiresAtMs(token: string): number | null {
  const parts = token.split('.')
  if (parts.length !== 3 || !parts[1]) return null
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = b64.padEnd(Math.ceil(b64.length / 4) * 4, '=')
    const exp = JSON.parse(atob(padded))?.exp
    return typeof exp === 'number' && Number.isFinite(exp) ? exp * 1000 : null
  } catch {
    return null
  }
}

export function isPastExpiry(token: string, nowMs: number = Date.now()): boolean {
  const expiresAt = tokenExpiresAtMs(token)
  return expiresAt !== null && expiresAt + EXPIRY_GRACE_MS < nowMs
}
